import { EnvironmentStatus } from '@onereach/billing-shared';
import { RouteRecordRaw } from 'vue-router';

import NotFound from '@/views/NotFound.vue';

import i18n from '../i18n/i18n';


// eslint-disable-next-line @typescript-eslint/no-explicit-any
const t = (key: string): string => (i18n.global as any).t(key);

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'root',
    meta: {
      requiresAuth: true,
    },
    redirect: {
      name: 'customers-dashboard',
    },
  },
  {
    name: 'customers-dashboard',
    path: '/customers',
    component: () => import('../views/CustomersDashboard.vue'),
    meta: {
      requiresAuth: true,
      permission: 'customers.view',
      title: t('sidebar.CustomersDashboard'),
    },
  },
  {
    name: 'contracts-dashboard',
    path: '/contracts',
    component: () => import('../views/ContractsDashboard.vue'),
    meta: {
      requiresAuth: true,
      permission: 'contracts.view',
      title: t('sidebar.ContractsDashboard'),
    },
  },
  {
    name: 'invoicing-dashboard',
    path: '/invoicing',
    component: () => import('../views/InvoicingDashboard.vue'),
    meta: {
      requiresAuth: true,
      permission: 'invoicing.view',
      title: t('sidebar.InvoicingDashboard'),
    },
  },
  {
    name: 'contacts',
    path: '/contacts',
    component: () => import('../views/ContactsView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'contacts.view',
      title: t('sidebar.Contacts'),
    },
  },
  {
    name: 'vendors',
    path: '/vendors',
    component: () => import('../views/VendorsView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'vendors.view',
      title: t('sidebar.Vendors'),
    },
  },
  {
    name: 'payment-providers',
    path: '/payment-providers',
    component: () => import('../views/PaymentProviders.vue'),
    meta: {
      requiresAuth: true,
      permission: 'payment-providers.view',
      title: t('sidebar.PaymentProviders'),
    },
  },
  {
    name: 'usage-recompilation',
    path: '/usage-recompilation',
    component: () => import('../views/UsageRecompilationView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'usage-recompilation.view',
      title: t('sidebar.UsageRecompilation'),
    },
  },
  {
    name: 'sessions-traffic',
    path: '/sessions-traffic',
    component: () => import('../views/SessionsTrafficView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'sessions-traffic.view',
      title: t('sidebar.SessionsTraffic'),
    },
  },
  {
    name: 'usage-traffic',
    path: '/usage-traffic',
    component: () => import('../views/UsageTrafficView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'usage-traffic.view',
      title: t('sidebar.UsageTraffic'),
    },
  },
  {
    name: 'usage-units',
    path: '/usage-units',
    component: () => import('../views/UsageUnitsView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'usage-traffic.view',
      title: t('sidebar.UsageUnits'),
    },
  },
  {
    name: 'costs',
    path: '/costs',
    component: () => import('../views/UsageCostView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'usage-cost.view',
      title: t('sidebar.Costs'),
    },
  },
  {
    name: 'aws-resources',
    path: '/aws-resources',
    component: () => import('../views/AwsResourcesView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'usage-cost.view',
      title: t('sidebar.AwsResources'),
    },
  },
  {
    name: 'threshold-management',
    path: '/threshold-management',
    component: () => import('../views/ThresholdManagement.vue'),
    meta: {
      requiresAuth: true,
      permission: 'threshold-management.view',
      title: t('sidebar.ThresholdManagement'),
    },
  },
  {
    name: 'reporting-databases',
    path: '/reporting-databases',
    component: () => import('../views/ReportingDatabasesDashoboard.vue'),
    meta: {
      requiresAuth: true,
      permission: 'reporting-database.view',
      title: t('sidebar.Databases'),
    },
  },
  {
    name: 'reconciliation',
    path: '/reconciliation',
    component: () => import('../views/ReconciliationView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'reconciliation.view',
      title: t('sidebar.Reconciliation'),
    },
  },
  {
    name: 'environments-dashboard',
    path: '/environments',
    component: () => import('../views/EnvironmentsDashboard.vue'),
    meta: {
      requiresAuth: true,
      permission: 'environments.view',
      title: t('sidebar.EnvironmentsDashboard'),
    },
    beforeEnter: (to) => {
      if (!to.query.status) {
        return {
          name: 'environments-dashboard',
          query: {
            ...to.query,
            status: EnvironmentStatus.ACTIVE,
          },
          replace: true,
        };
      }
    },
  },
  {
    name: 'services-dashboard',
    path: '/services',
    component: () => import('../views/ServicesDashboard.vue'),
    meta: {
      requiresAuth: true,
      permission: 'environments.view',
      title: t('sidebar.Services'),
    },
  },
  {
    name: 'accounts-dashboard',
    path: '/accounts',
    component: () => import('../views/AccountsDashboard.vue'),
    meta: {
      requiresAuth: true,
      permission: 'accounts.view',
      title: t('sidebar.AccountsDashboard'),
    },
  },
  {
    name: 'flows',
    path: '/flows',
    component: () => import('../views/FlowsView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'flows.view',
      title: t('sidebar.Flows'),
    },
  },
  {
    name: 'sku-mapping',
    path: '/sku-mapping',
    component: () => import('../views/MappingDashboard.vue'),
    meta: {
      requiresAuth: true,
      permission: 'sku-mapping.view',
      title: t('sidebar.MappingDashboard'),
    },
  },
  {
    name: 'sku-management',
    path: '/sku-management',
    component: () => import('../views/SkuManagementView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'sku.view',
      title: t('sidebar.SkuManagement'),
    },
  },
  {
    name: 'audit',
    path: '/audit',
    component: () => import('../views/AuditDashboard.vue'),
    meta: {
      requiresAuth: true,
      permission: 'audit.view',
      title: t('sidebar.Audit'),
    },
  },
  {
    name: 'login-audit',
    path: '/login-audit',
    component: () => import('../views/LoginAuditView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'audit.view',
      title: t('sidebar.LoginAudit'),
    },
  },
  {
    name: 'users',
    path: '/users',
    component: () => import('../views/UsersView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'users.view',
      title: t('sidebar.Users'),
    },
  },
  {
    name: 'errored-skus-dashboard',
    path: '/errored-skus',
    component: () => import('../views/ErroredSkusDashboard.vue'),
    meta: {
      requiresAuth: true,
      permission: 'errored-skus.view',
      title: i18n.global.t('sidebar.ErroredSkusDashboard'),
    },
  },
  {
    name: 'progressive-tiers',
    path: '/progressive-tiers',
    component: () => import(/* webpackChunkName: "progressive-tiers-view" */ '../views/ProgressiveTiersView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'progressive-tiers.view',
      title: t('sidebar.ProgressiveTiers'),
    },
  },
  {
    name: 'tiers-dashboard',
    path: '/tiers',
    component: () => import(/* webpackChunkName: "tiers-dashboard" */ '../views/TiersDashboard.vue'),
    meta: {
      requiresAuth: true,
      permission: 'tiers.view',
      title: t('sidebar.TiersDashboard'),
    },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: NotFound,
    meta: {
      requiresAuth: true,
    },
  },
  {
    name: 'sage-accrual',
    path: '/admin/sage-accrual',
    component: () => import('../views/SageJournalStatusDashboard.vue'),
    meta: {
      requiresAuth: true,
      permission: 'sage-accrual.view',
      title: t('sidebar.SageJournalStatus'),
    },
  },
];

export default routes;
